import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useWindowSize } from '@react-hook/window-size';
import { Amplitude, LogOnMount } from 'react-amplitude-hooks';
import { ROUTES } from 'Router/Routes';
import AssetCard from './AssetCard/AssetCard';
import Header from 'Components/shared/Header';
import SignupReminder from 'Features/Signup/SignupReminder';
import { AuthContext } from 'States/auth/authState';
import { CartContext } from 'States/cart/cartState';
import i18nextTranslate from 'Lang/i18nextTranslate';
import { i18nextKeys } from 'Lang/i18nextKeys';
import { UiContext } from 'States/ui/uiState';
import { ConfigContext } from 'States/config/configState';
import useKycTierCheck from 'Hooks/useKycTierCheck';
import CartButton from './CartButton';
import usePurchaseMethods from 'Hooks/usePurchaseMethods';

const Purchase = ({ match }) => {
  const history = useHistory();
  const {
    breakpoints: { sm },
  } = useContext(UiContext);
  const { isActive, isAuthenticated, isBlocked } = useContext(AuthContext);
  const { cartItems, loadCart, loadingCart } = useContext(CartContext);
  const {
    config: { kycTierFunctionalRestrictions },
  } = useContext(ConfigContext);
  const { isUserTierSufficient } = useKycTierCheck(
    kycTierFunctionalRestrictions?.visibility?.purchase
  );

  useEffect(() => {
    if (isActive) {
      if (!isUserTierSufficient) history.replace(ROUTES.welcome);
      loadCart();
    }
  }, [isActive]);

  const GRID_ITEM_WIDTH = 260;
  const GRID_ITEM_HEIGHT = 447;
  const GRID_GAP = 40;
  const [windowWidth, windowHeight] = useWindowSize({ wait: 300 });
  const { ref: pageEndRef, inView } = useInView({ rootMargin: "400px", threshold: 0.1 });
  const fetchLock = useRef(false);
  const pageSize = useMemo(() => {
    const width = windowWidth < 769 ? windowWidth : windowWidth - 300;
    const columns = Math.floor(
      (width + GRID_GAP) / (GRID_ITEM_WIDTH + GRID_GAP)
    );
    const rows = Math.ceil(
      (windowHeight * 1.5) / (GRID_ITEM_HEIGHT + GRID_GAP)
    );

    return Math.max(4, columns * rows);
  }, [windowWidth, windowHeight]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isInitialLoading
  } = usePurchaseMethods.purchaseMethodsPaginatedQuery({
    errorMessage: i18nextTranslate(i18nextKeys.errorPurchaseAssetLoadingError),
    pageSize
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !fetchLock.current) {
      fetchLock.current = true;
      fetchNextPage().finally(() => fetchLock.current = false);
    }
  }, [inView, hasNextPage, isFetchingNextPage]);

  return (
    <Amplitude
      eventProperties={{
        scope: 'purchase',
      }}
    >
      <LogOnMount eventType="Shop opened">
        <div data-qa="purchase">
          <Header
            text={i18nextTranslate(i18nextKeys.headerShop)}
            wrapChildren={false}
          >
            {isActive && (
              <CartButton
                itemsCount={Object.keys(cartItems).length}
                isLoading={loadingCart}
              />
            )}
          </Header>
          {!isInitialLoading && data?.pages.length ? (
            <>
              <div
                className="grid row-gap-32 col-gap-40 justify-center xl:px-24"
                style={{
                  gridTemplateColumns: sm
                    ? `repeat(auto-fit, ${GRID_ITEM_WIDTH}px)`
                    : 'repeat(auto-fit, 100%)',
                  gridTemplateRows: 'auto auto auto auto',
                  gap: `${GRID_GAP}px`,
                  placeItems: sm ? 'start' : 'center',
                }}
              >
                {data.pages.map((page, pageIndex) => {
                  return page?.assetsConfig.length ? (
                    page.assetsConfig.map((assetConfig, index) => (
                      <AssetCard
                        key={assetConfig.Id + pageIndex + index}
                        dataQa={`purchase-assetCard-${(pageIndex + 1) * (index + 1)}`}
                        assetConfig={assetConfig}
                        isCartModalOpen={assetConfig.Id === match.params.assetId}
                      />
                    ))
                  ) : null;
                })}
                {isAuthenticated && !isActive && !isBlocked && (
                  <SignupReminder
                    text={i18nextTranslate(i18nextKeys.signupNotificationPurchase)}
                  />
                )}
              </div>
              <div ref={pageEndRef} />
            </>
          ) : null}
        </div>
      </LogOnMount>
    </Amplitude>
  );
};

export default Purchase;
